import { useCallback, useEffect, useRef, useState } from 'react';
import aviso from '../lib/aviso';
import { MetaService } from '../services/MetaService';

/* O SDK do Facebook entra por <script> e mora em window.FB. Só o que o fluxo usa. */
interface FbLoginResponse { authResponse?: { code?: string } | null }
interface FbSdk {
    init: (o: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }) => void;
    login: (cb: (r: FbLoginResponse) => void, opts: Record<string, unknown>) => void;
}
declare global {
    interface Window { FB?: FbSdk; fbAsyncInit?: () => void }
}

interface Opcoes {
    /** App da Meta; sem ele o SDK não carrega. */
    appId: string | null | undefined;
    /** Configuração do Embedded Signup (coexistência com o app WhatsApp Business). */
    configurationId: string | null | undefined;
    /** Chamado depois que a API gravou a conexão. */
    aoConectar: () => void | Promise<void>;
}

/**
 * Conectar o WhatsApp pela API oficial da Meta (Embedded Signup em modo
 * coexistência). Saiu de dentro de Conexões para o Chat sem número usar o
 * mesmo fluxo: carrega o SDK, abre a janela da Meta e, quando ela devolve o
 * `code` e o par número/WABA, grava pela API.
 *
 * A Meta manda as duas metades por canais diferentes e em ordem incerta: o
 * `code` vem no callback do FB.login, o número/WABA vem por `postMessage`.
 * Quem chegar por último dispara a gravação.
 */
export function useConectarWhatsappMeta({ appId, configurationId, aoConectar }: Opcoes) {
    const [pronto, setPronto] = useState(false);
    const [conectando, setConectando] = useState(false);
    const dados = useRef<{ code?: string; phoneNumberId?: string; wabaId?: string }>({});
    const aoConectarRef = useRef(aoConectar);
    aoConectarRef.current = aoConectar;

    const gravar = useCallback(async (code: string, phoneNumberId: string, wabaId: string) => {
        setConectando(true);
        try {
            const r = await MetaService.configurarCoexistencia({ code, phoneNumberId, wabaId });
            if (r.sucesso) {
                aviso.ok('WhatsApp conectado pela API oficial da Meta.');
                await aoConectarRef.current();
            } else {
                aviso.erro(r.mensagem || 'Não foi possível conectar com a Meta.');
            }
        } catch {
            aviso.erro('Não foi possível conectar com a Meta.');
        } finally {
            setConectando(false);
            dados.current = {};
        }
    }, []);

    useEffect(() => {
        if (!appId) return;
        const iniciar = () => {
            window.FB?.init({ appId, autoLogAppEvents: true, xfbml: true, version: 'v26.0' });
            setPronto(true);
        };
        if (window.FB) iniciar();
        else {
            window.fbAsyncInit = iniciar;
            if (!document.querySelector('script[src="https://connect.facebook.net/pt_BR/sdk.js"]')) {
                const s = document.createElement('script');
                s.src = 'https://connect.facebook.net/pt_BR/sdk.js';
                s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
                document.body.appendChild(s);
            }
        }
        const aoReceber = (event: MessageEvent) => {
            if (!event.origin.endsWith('facebook.com')) return;
            try {
                const data = (typeof event.data === 'string' ? JSON.parse(event.data) : event.data) as
                    { type?: string; event?: string; data?: { phone_number_id?: string; waba_id?: string } };
                if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
                if (data.event === 'FINISH' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
                    const phoneNumberId = data.data?.phone_number_id;
                    const wabaId = data.data?.waba_id;
                    if (phoneNumberId && wabaId) {
                        dados.current.phoneNumberId = phoneNumberId;
                        dados.current.wabaId = wabaId;
                        if (dados.current.code) gravar(dados.current.code, phoneNumberId, wabaId);
                    }
                }
            } catch { /* mensagem que não é nossa */ }
        };
        window.addEventListener('message', aoReceber);
        return () => window.removeEventListener('message', aoReceber);
    }, [appId, gravar]);

    const conectar = useCallback(() => {
        if (!appId || !configurationId) { aviso.erro('Configurações da Meta não carregadas.'); return; }
        if (!window.FB) { aviso.erro('SDK do Facebook não carregado.'); return; }
        window.FB.login(
            (response) => {
                const code = response.authResponse?.code;
                if (code) {
                    dados.current.code = code;
                    if (dados.current.phoneNumberId && dados.current.wabaId) {
                        gravar(code, dados.current.phoneNumberId, dados.current.wabaId);
                    }
                }
            },
            {
                config_id: configurationId,
                response_type: 'code',
                override_default_response_type: true,
                extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' },
            },
        );
    }, [appId, configurationId, gravar]);

    return { pronto: pronto && !!configurationId, conectando, conectar };
}
